"use client"

import { createContext, useState, useEffect } from "react"
import axios from "axios"
import { API_URL } from "../config"

export const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load user on initial render
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem("token")

        if (!token) {
          setLoading(false)
          return
        }

        // Set default headers for all requests
        axios.defaults.headers.common["x-auth-token"] = token

        const res = await axios.get(`${API_URL}/api/auth`)

        setUser(res.data)
        setIsAuthenticated(true)
      } catch (err) {
        localStorage.removeItem("token")
        setError(err.response?.data?.msg || "Authentication failed")
        setIsAuthenticated(false)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [])

  // Register user
  const register = async (formData) => {
    try {
      setLoading(true)
      const res = await axios.post(`${API_URL}/api/users`, formData)

      localStorage.setItem("token", res.data.token)
      axios.defaults.headers.common["x-auth-token"] = res.data.token

      await loadUser()
      return { success: true }
    } catch (err) {
      setError(err.response?.data?.msg || "Registration failed")
      return { success: false, error: err.response?.data?.msg || "Registration failed" }
    } finally {
      setLoading(false)
    }
  }

  // Login user
  const login = async (email, password) => {
    try {
      setLoading(true);
  
      const res = await axios.post(`${API_URL}/api/auth`, { email, password });
  
      if (res.status !== 200) {
        throw new Error("Unexpected response status");
      }
  
      const token = res.data.token;
      localStorage.setItem("token", token);
      axios.defaults.headers.common["x-auth-token"] = token;
  
      const userRes = await axios.get(`${API_URL}/api/auth`);
      setUser(userRes.data);
      setIsAuthenticated(true);
      setError(null);
  
      return { success: true };
  
    } catch (err) {
      const errorMsg = err.response?.data?.msg || "Login failed";
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };
  
  // const login = async (email, password) => {
  //   try {
  //     setLoading(true)
  //     const res = await axios.post(`${API_URL}/api/auth`, { email, password })

  //     localStorage.setItem("token", res.data.token)
  //     axios.defaults.headers.common["x-auth-token"] = res.data.token

  //     const userRes = await axios.get(`${API_URL}/api/auth`)
  //     setUser(userRes.data)
  //     setIsAuthenticated(true)
  //     setError(null)
  //     return { success: true }
  //   } catch (err) {
  //     setError(err.response?.data?.msg || "Login failed")
  //     return { success: false, error: err.response?.data?.msg || "Login failed" }
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  // Logout user
  const logout = () => {
    localStorage.removeItem("token")
    delete axios.defaults.headers.common["x-auth-token"]
    setUser(null)
    setIsAuthenticated(false)
  }

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true)
      const res = await axios.put(`${API_URL}/api/profile/update`, profileData)
      setUser({ ...user, profile: res.data })
      return { success: true, data: res.data }
    } catch (err) {
      setError(err.response?.data?.msg || "Profile update failed")
      return { success: false, error: err.response?.data?.msg || "Profile update failed" }
    } finally {
      setLoading(false)
    }
  }

  // Check if user is admin
  const isAdmin = () => {
    return user && user.role === "admin"
  }

  const loadUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth`)
      setUser(res.data)
      setIsAuthenticated(true)
      setError(null)
    } catch (err) {
      localStorage.removeItem("token")
      setError(err.response?.data?.msg || "Authentication failed")
      setIsAuthenticated(false)
      setUser(null)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        error,
        register,
        login,
        logout,
        updateProfile,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
